import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Card, Typography } from 'antd'
import {
  UserOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  FireOutlined,
} from '@ant-design/icons'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  AreaChart,
  Area,
} from 'recharts'
import AnalyticsChart from '../AnalyticsChart'
import { transformUserReportData, formatSecondsToReadable } from './userReportDataTransformer'

const ANALYTICS_COLORS = {
  primary: '#0a1227',
  secondary: '#7d9bc1',
  accent: '#D02139',
  gridColor: '#e8e8e8',
}

const { Text } = Typography

// Custom Tooltip component for consistent Delta theme
class CustomChartTooltip extends Component {
  render() {
    const { active, payload, label, unit = '', valueLabel = 'Records', valueFormatter } = this.props
    if (active && payload && payload.length) {
      const item = payload[0]
      const displayValue = valueFormatter ? valueFormatter(item) : `${item.value} ${unit}`.trim()
      return (
        <div
          style={{
            background: 'rgba(10, 18, 39, 0.95)',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontSize: 12,
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: 600,
              borderBottom: '1px solid rgba(255,255,255,0.2)',
              paddingBottom: 4,
            }}
          >
            {label}
          </p>
          <p
            style={{
              margin: '4px 0 0',
              color: '#7d9bc1',
            }}
          >
            {valueLabel}: <strong style={{ color: '#ffffff' }}>{displayValue}</strong>
          </p>
          {item.payload?.percentage !== undefined && (
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 11,
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              Share: {item.payload.percentage}%
            </p>
          )}
        </div>
      )
    }
    return null
  }
}

CustomChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string,
  valueLabel: PropTypes.string,
  valueFormatter: PropTypes.func,
}

class UserReportAnalytics extends Component {
  constructor(props) {
    super(props)
    this.state = { activeTab: 'daily_activity' }
  }

  handleTabChange = (activeTab) => {
    this.setState({ activeTab })
  }

  render() {
    const { data = [] } = this.props
    const analytics = transformUserReportData(data)

    const {
      kpis,
      dailyActivity,
      moduleUsage,
      topUsers,
      uniqueUsersTrend,
      activityByHour,
      timeSpentByModule,
      hasValidTimeSpent,
      isEmpty,
    } = analytics

    if (isEmpty) {
      return (
        <div
          style={{
            padding: '40px 0',
            textAlign: 'center',
          }}
        >
          <Text type='secondary'>No user activity records found for the selected period.</Text>
        </div>
      )
    }

    const top10Users = topUsers.slice(0, 10)

    return (
      <div
        className='user-report-analytics-container'
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* 1. Compact Summary / KPI Cards */}
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                boxShadow: '0 1px 3px rgba(10, 18, 39, 0.04)',
                height: '100%',
              }}
              bodyStyle={{ padding: '10px 14px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Total Activity
                </span>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'rgba(10, 18, 39, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ThunderboltOutlined
                    style={{
                      color: '#0a1227',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#0a1227',
                  lineHeight: 1.2,
                }}
              >
                {kpis.totalRecords.toLocaleString()}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                boxShadow: '0 1px 3px rgba(10, 18, 39, 0.04)',
                height: '100%',
              }}
              bodyStyle={{ padding: '10px 14px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active Users
                </span>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'rgba(125, 155, 193, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UserOutlined
                    style={{
                      color: '#0a1227',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#0a1227',
                  lineHeight: 1.2,
                }}
              >
                {kpis.uniqueUsers.toLocaleString()}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                boxShadow: '0 1px 3px rgba(10, 18, 39, 0.04)',
                height: '100%',
              }}
              bodyStyle={{ padding: '10px 14px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Modules Used
                </span>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'rgba(10, 18, 39, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppstoreOutlined
                    style={{
                      color: '#0a1227',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: '#0a1227',
                  lineHeight: 1.2,
                }}
              >
                {kpis.uniqueModules.toLocaleString()}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={12} md={6}>
            <Card
              bordered={false}
              style={{
                borderRadius: 8,
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                boxShadow: '0 1px 3px rgba(10, 18, 39, 0.04)',
                height: '100%',
              }}
              bodyStyle={{ padding: '10px 14px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Most Used Module
                </span>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'rgba(208, 33, 57, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FireOutlined
                    style={{
                      color: '#D02139',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: '#0a1227',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={kpis.mostUsedModule}
              >
                {kpis.mostUsedModule}
                {kpis.mostUsedModuleCount > 0 && (
                  <span
                    style={{
                      fontSize: 11,
                      color: '#D02139',
                      fontWeight: 600,
                      marginLeft: 6,
                    }}
                  >
                    ({kpis.mostUsedModuleCount.toLocaleString()})
                  </span>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* 2. Graph Navigation Tabs */}
        <div className='analytics-tabs-wrapper'>
          <div className='analytics-tabs-container'>
            <div
              className={`analytics-tab-item ${this.state.activeTab === 'daily_activity' ? 'active-analytics-tab' : ''}`}
              onClick={() => this.handleTabChange('daily_activity')}
            >
              <span>Daily Activity Trend</span>
            </div>
            <div
              className={`analytics-tab-item ${this.state.activeTab === 'unique_users' ? 'active-analytics-tab' : ''}`}
              onClick={() => this.handleTabChange('unique_users')}
            >
              <span>Unique Users Over Time</span>
            </div>
            <div
              className={`analytics-tab-item ${this.state.activeTab === 'module_usage' ? 'active-analytics-tab' : ''}`}
              onClick={() => this.handleTabChange('module_usage')}
            >
              <span>Module Usage</span>
            </div>
            <div
              className={`analytics-tab-item ${this.state.activeTab === 'top_users' ? 'active-analytics-tab' : ''}`}
              onClick={() => this.handleTabChange('top_users')}
            >
              <span>Most Active Users</span>
            </div>
            <div
              className={`analytics-tab-item ${this.state.activeTab === 'hourly_activity' ? 'active-analytics-tab' : ''}`}
              onClick={() => this.handleTabChange('hourly_activity')}
            >
              <span>Activity by Time of Day</span>
            </div>
            {hasValidTimeSpent && (
              <div
                className={`analytics-tab-item ${this.state.activeTab === 'time_spent' ? 'active-analytics-tab' : ''}`}
                onClick={() => this.handleTabChange('time_spent')}
              >
                <span>Time Spent by Module</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Active Single Chart View */}
        <div className='analytics-chart-viewport'>
          {this.state.activeTab === 'daily_activity' && (
            <AnalyticsChart
              title='Daily Activity Trend'
              subtitle='Total application activity records over time'
              info='Shows how user activity volume fluctuates day by day across the selected date range'
              height={480}
              isEmpty={dailyActivity.length === 0}
            >
              <LineChart
                data={dailyActivity}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} vertical={false} />
                <XAxis
                  dataKey='displayDate'
                  stroke='#8c8c8c'
                  fontSize={11}
                  tickLine={false}
                  dy={8}
                  angle={dailyActivity.length > 10 ? -30 : 0}
                  textAnchor={dailyActivity.length > 10 ? 'end' : 'middle'}
                />
                <YAxis stroke='#8c8c8c' fontSize={11} tickLine={false} allowDecimals={false} />
                <RechartsTooltip content={<CustomChartTooltip valueLabel='Activity Count' />} />
                <Legend verticalAlign='top' height={30} iconType='circle' />
                <Line
                  type='monotone'
                  dataKey='count'
                  name='Activity Records'
                  stroke={ANALYTICS_COLORS.primary}
                  strokeWidth={2.5}
                  dot={{
                    r: 3,
                    fill: ANALYTICS_COLORS.primary,
                  }}
                  activeDot={{
                    r: 6,
                    fill: ANALYTICS_COLORS.accent,
                  }}
                />
              </LineChart>
            </AnalyticsChart>
          )}

          {this.state.activeTab === 'unique_users' && (
            <AnalyticsChart
              title='Unique Active Users Over Time'
              subtitle='Count of distinct users active per day'
              info='Counts each user only once per day regardless of the number of actions performed'
              height={480}
              isEmpty={uniqueUsersTrend.length === 0}
            >
              <AreaChart
                data={uniqueUsersTrend}
                margin={{
                  top: 10,
                  right: 30,
                  left: 0,
                  bottom: 20,
                }}
              >
                <defs>
                  <linearGradient id='userColorGrad' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={ANALYTICS_COLORS.primary} stopOpacity={0.35} />
                    <stop offset='95%' stopColor={ANALYTICS_COLORS.primary} stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} vertical={false} />
                <XAxis
                  dataKey='displayDate'
                  stroke='#8c8c8c'
                  fontSize={11}
                  tickLine={false}
                  dy={8}
                  angle={uniqueUsersTrend.length > 10 ? -30 : 0}
                  textAnchor={uniqueUsersTrend.length > 10 ? 'end' : 'middle'}
                />
                <YAxis stroke='#8c8c8c' fontSize={11} tickLine={false} allowDecimals={false} />
                <RechartsTooltip content={<CustomChartTooltip valueLabel='Unique Users' unit='users' />} />
                <Legend verticalAlign='top' height={30} iconType='circle' />
                <Area
                  type='monotone'
                  dataKey='count'
                  name='Unique Users'
                  stroke={ANALYTICS_COLORS.primary}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill='url(#userColorGrad)'
                />
              </AreaChart>
            </AnalyticsChart>
          )}

          {this.state.activeTab === 'module_usage' && (
            <AnalyticsChart
              title='Module Usage'
              subtitle='Access count by application module'
              info='Distribution of user activities across modules, sorted from most to least accessed'
              height={Math.max(480, moduleUsage.length * 28)}
              isEmpty={moduleUsage.length === 0}
            >
              <BarChart
                layout='vertical'
                data={moduleUsage}
                margin={{
                  top: 10,
                  right: 30,
                  left: 30,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} horizontal={false} />
                <XAxis type='number' stroke='#8c8c8c' fontSize={11} tickLine={false} allowDecimals={false} />
                <YAxis
                  type='category'
                  dataKey='module'
                  stroke='#595959'
                  fontSize={11}
                  tickLine={false}
                  interval={0}
                  width={150}
                />
                <RechartsTooltip content={<CustomChartTooltip valueLabel='Module Hits' />} />
                <Bar dataKey='count' name='Accesses' fill={ANALYTICS_COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </AnalyticsChart>
          )}

          {this.state.activeTab === 'top_users' && (
            <AnalyticsChart
              title='Most Active Users'
              subtitle='Top users by total activity records'
              info='Identifies high-frequency users based on record count'
              height={Math.max(480, top10Users.length * 32)}
              isEmpty={top10Users.length === 0}
            >
              <BarChart
                layout='vertical'
                data={top10Users}
                margin={{
                  top: 10,
                  right: 30,
                  left: 30,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} horizontal={false} />
                <XAxis type='number' stroke='#8c8c8c' fontSize={11} tickLine={false} allowDecimals={false} />
                <YAxis
                  type='category'
                  dataKey='name'
                  stroke='#595959'
                  fontSize={11}
                  tickLine={false}
                  interval={0}
                  width={150}
                />
                <RechartsTooltip content={<CustomChartTooltip valueLabel='Activity Records' />} />
                <Bar dataKey='count' name='Actions' fill={ANALYTICS_COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </AnalyticsChart>
          )}


          {this.state.activeTab === 'hourly_activity' && (
            <AnalyticsChart
              title='Activity by Time of Day'
              subtitle='Distribution of actions across 24 hours'
              info='Shows peak operating hours based on login and action timestamps'
              height={480}
              isEmpty={activityByHour.every((h) => h.count === 0)}
            >
              <BarChart
                data={activityByHour}
                margin={{
                  top: 10,
                  right: 20,
                  left: 0,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} vertical={false} />
                <XAxis
                  dataKey='hour'
                  stroke='#8c8c8c'
                  fontSize={11}
                  tickLine={false}
                  interval={1}
                  angle={-45}
                  textAnchor='end'
                  dy={6}
                />
                <YAxis stroke='#8c8c8c' fontSize={11} tickLine={false} allowDecimals={false} />
                <RechartsTooltip content={<CustomChartTooltip valueLabel='Actions at Hour' />} />
                <Bar dataKey='count' name='Activity Count' fill={ANALYTICS_COLORS.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </AnalyticsChart>
          )}

          {hasValidTimeSpent && this.state.activeTab === 'time_spent' && (
            <AnalyticsChart
              title='Time Spent by Module'
              subtitle='Total duration spent across modules'
              info='Aggregated time duration from recorded session metrics'
              height={Math.max(480, timeSpentByModule.length * 28)}
              isEmpty={timeSpentByModule.length === 0}
            >
              <BarChart
                layout='vertical'
                data={timeSpentByModule}
                margin={{
                  top: 10,
                  right: 30,
                  left: 30,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke={ANALYTICS_COLORS.gridColor} horizontal={false} />
                <XAxis
                  type='number'
                  stroke='#8c8c8c'
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(val) => {
                    const h = Math.floor(val / 3600)
                    const m = Math.floor((val % 3600) / 60)
                    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
                    return `${m}m`
                  }}
                />
                <YAxis
                  type='category'
                  dataKey='module'
                  stroke='#595959'
                  fontSize={11}
                  tickLine={false}
                  interval={0}
                  width={150}
                />
                <RechartsTooltip
                  content={
                    <CustomChartTooltip
                      valueLabel='Total Time'
                      valueFormatter={(item) => item.payload?.totalFormatted || formatSecondsToReadable(item.value)}
                    />
                  }
                />
                <Bar dataKey='totalSeconds' name='Time Spent' fill={ANALYTICS_COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </AnalyticsChart>
          )}
        </div>
      </div>
    )
  }
}

UserReportAnalytics.propTypes = { data: PropTypes.array.isRequired }

export default UserReportAnalytics

